/**
 * @flow
 */

import React from 'react';
import gql from 'graphql-tag';
import { useQuery, useApolloClient } from '@apollo/client';
import { useSelector } from 'react-redux';
import { getFreeTimeSpans } from '../../../../lib/schedule';
import { format } from 'date-fns';
import { convertMinutesToDateTime } from '../../../../lib/date';
import { map } from 'lodash';
import { TimeSpanSelect as BaseTimeSpanSelect } from '../../../../components';

// Query to get salon settings (read from local cache)
const GET_SALON_QUERY = gql`
    query getSalonSettings($salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    settings {
                        schedule {
                            startAt
                            endAt
                            step
                            format
                        }
                    }
                }
            }
        }
    }
`;

// Query to get schedule for given date for specific master
const GET_MASTER_QUERY = gql`
    query getMaster($master: ID!, $date: String!, $salon: ID!) {
        viewer {
            id
            ... on Administrator {
                salon(id: $salon) {
                    id
                    master(id: $master) {
                        id
                        schedule(startDate: $date) {
                            startAt
                            endAt
                        }
                    }
                }
            }
        }
    }
`;

function TimeSpanSelect({
    meta: _meta,
    master: masterProp,
    name,
    dispatch: _dispatch,
    startAtValue,
    endAtValue,
    ...props
}) {
    const salon = useSelector(({ user }) => user.get('salon'));
    const date = useSelector(({ schedule }) => schedule.get('date'));

    const apolloClient = useApolloClient();
    const { loading, data } = useQuery(GET_MASTER_QUERY, {
        variables: {
            salon,
            master: masterProp.id,
            date,
        },
    });

    const master =
        !loading && data && data.viewer ? data.viewer.salon.master : null;

    let options = null;
    if (!loading && master) {
        let salonData;
        try {
            salonData = apolloClient.readQuery({
                query: GET_SALON_QUERY,
                variables: { salon },
            });
        } catch (e) {
            // Cache miss is acceptable
        }
        if (salonData) {
            const { schedule } = salonData.viewer.salon.settings;
            const index = name === 'startAt' ? 0 : 1;

            const exclude = map(master.schedule, workingTime => [
                workingTime.startAt,
                workingTime.endAt,
            ]).filter(
                timeSpan =>
                    timeSpan[index] < startAtValue &&
                    timeSpan[index] > endAtValue,
            );

            const freeTimeSpans = getFreeTimeSpans({
                timeSpan: [schedule.startAt, schedule.endAt],
                step: schedule.step,
                exclude,
            });

            options = map(freeTimeSpans, timeSpan => {
                const time = convertMinutesToDateTime(timeSpan[index], date);
                return {
                    text: format(time, schedule.format),
                    value: time.toISOString(),
                };
            });
        }
    }

    return <BaseTimeSpanSelect options={options} {...props} />;
}

export default TimeSpanSelect;
